import {
  AggregateFunction,
  CountOperator,
  type RelatedResourceAggregate,
  type RelativeDate,
} from '@reach/shared-types/conditions';
import type { FieldInfo } from '@reach/shared-types/segment-schema-graph';
import { Trash2 } from 'lucide-react';
import React from 'react';
import { aggregatableFields, defaultValueForField } from './aggregate-fields';
import { FieldSelector } from './FieldSelector';
import { RelativeDateBuilder } from './RelativeDateBuilder';
import styles from './SegmentVisualBuilder.module.css';

const AGGREGATE_FUNCTION_OPTIONS: { value: AggregateFunction; label: string }[] = [
  { value: AggregateFunction.SUM, label: 'sum' },
  { value: AggregateFunction.AVG, label: 'average' },
  { value: AggregateFunction.MIN, label: 'lowest' },
  { value: AggregateFunction.MAX, label: 'highest' },
];

const NUMBER_COMPARISON_OPTIONS: { value: CountOperator; label: string }[] = [
  { value: CountOperator.GT, label: 'more than' },
  { value: CountOperator.GTE, label: 'at least' },
  { value: CountOperator.EQ, label: 'exactly' },
  { value: CountOperator.LTE, label: 'at most' },
  { value: CountOperator.LT, label: 'less than' },
];

const DATE_COMPARISON_OPTIONS: { value: CountOperator; label: string }[] = [
  { value: CountOperator.GT, label: 'after' },
  { value: CountOperator.GTE, label: 'on or after' },
  { value: CountOperator.EQ, label: 'on' },
  { value: CountOperator.LTE, label: 'on or before' },
  { value: CountOperator.LT, label: 'before' },
];

interface AggregateRowProps {
  aggregate: RelatedResourceAggregate;
  fieldOptions: FieldInfo[];
  onChange: (aggregate: RelatedResourceAggregate) => void;
  onRemove: () => void;
  disabled?: boolean;
  readOnly?: boolean;
}

export const AggregateRow: React.FC<AggregateRowProps> = ({
  aggregate,
  fieldOptions,
  onChange,
  onRemove,
  disabled = false,
  readOnly = false,
}) => {
  const inputsDisabled = disabled || readOnly;
  const eligibleFields = aggregatableFields(fieldOptions, aggregate.function);
  const comparesDates = typeof aggregate.value !== 'number';
  const comparisonOptions = comparesDates ? DATE_COMPARISON_OPTIONS : NUMBER_COMPARISON_OPTIONS;

  const handleFunctionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextFunction = e.target.value as AggregateFunction;
    const nextEligibleFields = aggregatableFields(fieldOptions, nextFunction);
    const currentFieldStillEligible = nextEligibleFields.some(field => field.name === aggregate.field);
    if (currentFieldStillEligible) {
      onChange({ ...aggregate, function: nextFunction });
      return;
    }
    const replacementField = nextEligibleFields[0];
    onChange({
      ...aggregate,
      function: nextFunction,
      field: replacementField?.name ?? '',
      value: defaultValueForField(replacementField),
    });
  };

  const handleFieldChange = (fieldName: string) => {
    const nextField = fieldOptions.find(field => field.name === fieldName);
    const nextFieldIsDate = nextField?.type === 'date';
    const valueKindChanges = nextFieldIsDate !== comparesDates;
    onChange({
      ...aggregate,
      field: fieldName,
      ...(valueKindChanges && { value: defaultValueForField(nextField) }),
    });
  };

  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...aggregate, operator: e.target.value as CountOperator });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseFloat(e.target.value);
    if (!Number.isNaN(parsedValue)) {
      onChange({ ...aggregate, value: parsedValue });
    }
  };

  const handleRelativeDateChange = (value: RelativeDate) => {
    onChange({ ...aggregate, value });
  };

  return (
    <div className={styles.measureRow}>
      <select
        className={styles.measureSelect}
        value={aggregate.function}
        onChange={handleFunctionChange}
        disabled={inputsDisabled}
        aria-label="Aggregate function">
        {AGGREGATE_FUNCTION_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.subjectPhrase}>of</span>
      <div className={styles.measureField}>
        <FieldSelector
          options={eligibleFields}
          value={aggregate.field}
          onChange={handleFieldChange}
          placeholder="Select field..."
          disabled={inputsDisabled}
        />
      </div>
      <select
        className={styles.measureSelect}
        value={aggregate.operator}
        onChange={handleOperatorChange}
        disabled={inputsDisabled}
        aria-label="Aggregate operator">
        {comparisonOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {typeof aggregate.value === 'number' ? (
        <input
          type="number"
          step="any"
          value={aggregate.value}
          onChange={handleNumberChange}
          disabled={inputsDisabled}
          className={styles.measureNumberInput}
          aria-label="Aggregate value"
        />
      ) : (
        <RelativeDateBuilder
          value={aggregate.value}
          onChange={handleRelativeDateChange}
          disabled={inputsDisabled}
        />
      )}
      {!readOnly && (
        <button
          type="button"
          className={styles.relatedTitleButton}
          onClick={onRemove}
          disabled={inputsDisabled}
          aria-label="Remove aggregate">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
};
